// Angular-free helpers for building GCS object keys and uploading image blobs.
// Kept clear of Angular service imports (HttpClient etc.) so the key-building
// and upload-failure logic can be unit-tested in isolation under mocha/ts-node
// — see image-upload-helpers.test.ts. ImageService delegates to these.

import { buildOwnedImagePrefix } from '@cf-shared/storage-object-path';
import { ImageSize } from '@cf-shared/enums';
import { markCallerReportsFailure } from 'app/fetch-failure-classifier';

/** One canvas size an upload should produce, in pixels. */
export interface ImageVariantSize {
  width: number;
  height: number;
}

/** What `planImageVariants` answers: which minimum applied, whether the source
 * clears it, and the canvas sizes to produce. */
export interface ImageVariantPlan {
  /** The minimum shorter-edge size that applied to this source, in pixels. The
   * caller needs it to build the user-facing "your image is too small" message,
   * which names the number. */
  minShorterEdge: number;
  /** True when the source's shorter edge is under `minShorterEdge`. Only the
   * file-picker upload path acts on this; the label-scan path deliberately
   * ignores it, because it has never applied a minimum. */
  sourceTooSmall: boolean;
  /** The canvas sizes to produce, in ladder order. Empty when the source is
   * smaller than every rung of the ladder — the caller treats that as a failed
   * upload rather than saving a picture with no sizes. */
  variants: ImageVariantSize[];
}

/** The multi-size ladder, smallest first. */
const SIZE_LADDER: number[] = [ImageSize.Thumbnail, ImageSize.Small, ImageSize.Medium, ImageSize.Large];

/**
 * Decide which image sizes an upload should produce from one source image.
 *
 * This is the whole rule, in one place and with no browser in it: it takes two
 * numbers and a flag and answers with a list, so a unit test can ask it a
 * question in microseconds instead of a person hand-uploading photographs of
 * several sizes on several platforms. Before it existed these decisions were
 * spread across two methods of `image-service.ts`, inside a promise executor
 * inside a forEach inside the upload method, and three separate commits had to
 * repair them (`f05986cb0`, `0dd181910`, `f60b1f82d`).
 *
 * `multipleSizes` true means the standard ladder (Thumbnail / Small / Medium /
 * Large), filtered to the rungs the source is big enough for. False means ONE
 * image at the source's own width, capped at Large — so a small image such as a
 * coach logo is produced at its native size instead of being skipped for being
 * under 1080px, which used to leave a logo with no uploaded size at all and
 * therefore no URL to save.
 */
export function planImageVariants(params: {
  sourceWidth: number;
  sourceHeight: number;
  multipleSizes: boolean;
  /** Overrides the size-mode default below. The coach-branding logo uploader
   * passes one, because a small logo is fine. */
  minShorterEdge?: number;
}): ImageVariantPlan {
  const { sourceWidth, sourceHeight, multipleSizes } = params;
  // minimum accepted shorter-edge size: an explicit override if given,
  // otherwise the size-mode default (single-size needs a larger source since it
  // is not downscaled; multi-size only needs Small).
  const minShorterEdge = params.minShorterEdge ?? (multipleSizes ? ImageSize.Small : ImageSize.Large);
  const sourceTooSmall = Math.min(sourceWidth, sourceHeight) < minShorterEdge;

  // output widths to produce
  const targetWidths: number[] = multipleSizes ? SIZE_LADDER : [Math.min(ImageSize.Large, sourceWidth)];

  const variants: ImageVariantSize[] = [];
  for (const width of targetWidths) {
    // if this is a ladder rung larger than the source, skip it (never upscale).
    // the single-size width is already clamped to the source, so it never skips.
    if (multipleSizes && (sourceWidth < width || sourceHeight < width)) {
      continue;
    }
    // the square thumbnail is only meant for the multi-size ladder's Thumbnail
    // rung. don't square a single-size output just because its width happens to
    // equal 256 (e.g. a 256px-wide logo).
    if (multipleSizes && width === ImageSize.Thumbnail) {
      variants.push({ width, height: width });
      continue;
    }
    // otherwise keep the source's aspect ratio. whole numbers, because that is
    // what a canvas becomes once these are assigned to it.
    //
    // The two-step division is deliberate and must not be "simplified" to
    // `sourceHeight * width / sourceWidth`. The code this replaced divided
    // twice — it computed a scale factor and then divided by it — and dividing
    // twice rounds twice, so when the exact answer is a whole number this form
    // can land one bit below it and truncate down by one. A 481x481 source at
    // the 480 rung comes out 480x479 here and 480x480 the one-step way. That
    // makes the one-step form the more correct of the two, and it is still not
    // what goes here: this function was extracted to change nothing, and a
    // 1px difference on roughly one source in 5,600 is a behaviour change that
    // belongs in its own reviewed commit rather than hidden inside a move.
    const scale = sourceWidth / width;
    variants.push({ width, height: Math.trunc(sourceHeight / scale) });
  }

  return { minShorterEdge, sourceTooSmall, variants };
}

/**
 * The size to treat a scan picture as when planning its upload: the picture
 * itself when its shorter side already reaches `minShorterEdge`, otherwise the
 * same shape scaled up until it does.
 *
 * The label and recipe scans upload a picture the person never chose for its
 * size, and a small one (a library image, or an uncropped tiny photo) used to
 * clear no rung of the ladder at all, so the food was saved with no photo
 * (df-5fbd3836e3a3, Sentry CYBERNETIC-FITNESS-APP-464). Planning against this
 * size makes the canvas draw scale the picture up instead. A soft photo is
 * better than none. The file-picker path does not use this: it refuses a small
 * image with a message on purpose.
 */
export function upscaleToShorterEdge(sourceWidth: number, sourceHeight: number, minShorterEdge: number): { width: number; height: number } {
  const shorterEdge = Math.min(sourceWidth, sourceHeight);
  // if the picture is already big enough (or has no size at all), keep it as it is
  if (shorterEdge >= minShorterEdge || shorterEdge <= 0) {
    return { width: sourceWidth, height: sourceHeight };
  }
  // the shorter side lands exactly on the minimum; the longer side keeps the shape,
  // rounded up so it can never fall below the minimum either
  const scale = minShorterEdge / shorterEdge;
  if (sourceWidth <= sourceHeight) {
    return { width: minShorterEdge, height: Math.max(minShorterEdge, Math.ceil(sourceHeight * scale)) };
  }
  return { width: Math.max(minShorterEdge, Math.ceil(sourceWidth * scale)), height: minShorterEdge };
}

/**
 * Build the GCS object key for one size variant of an uploaded image.
 *
 * `fileName` is "<collection>/<hashed-id>.<ext>"; `size` is the variant width.
 * `ownerToken` is the uploader's storage owner token — see
 * `storage-owner-token.ts`. The server refuses to delete or overwrite a key
 * whose token is not the caller's, so a key minted without one can never be
 * deleted again (#5016).
 *
 * The result deliberately has NO leading slash: the object key is appended
 * after the bucket name in the signed URL ("<bucket>/<key>"), so a leading
 * slash bakes an empty path segment in ("<bucket>//images/...") — issue #3707.
 * The shared `buildOwnedImagePrefix` builds the owned part, so the shape the
 * server parses and the shape minted here cannot drift apart.
 */
export function buildImageVariantKey(fileName: string, ownerToken: string, size?: number): string {
  // split "<name>.<ext>". match is null when fileName has no extension —
  // generateImageId returns a bare hash for source names without one — so fall
  // back to the whole name and default the extension to jpg (the canvas output
  // format) rather than dereferencing a null match and throwing.
  const match = fileName.match(/(.+)\.([0-9a-z]+)(?:[\?#]|$)/i);
  const fileNameWithoutExtension = match ? match[1] : fileName;
  const fileExtension = match ? match[2] : 'jpg';
  return `${buildOwnedImagePrefix(ownerToken)}${fileNameWithoutExtension}__w=${size}.${fileExtension}`;
}

/**
 * The PUT reached the storage server and got a non-OK HTTP status back. Its own class so a caller
 * can tell it from a rejected fetch, which the global fetch wrapper no longer reports for this
 * request (it is marked as caller-reported).
 */
export class ImageUploadHttpError extends Error {
  constructor(public readonly status: number, path: string) {
    super(`Image upload failed (HTTP ${status}) for ${path}`);
    this.name = 'ImageUploadHttpError';
  }
}

/**
 * PUT one image blob to its signed GCS URL. On a rejected fetch (e.g. a
 * CORS-blocked upload) or a non-OK HTTP status it calls `onError` with the
 * error (to report it to Sentry) and then rethrows, so the caller can also
 * surface the failure to the user instead of saving an image that points at a
 * missing/broken object.
 */
export async function putImageToSignedUrl(
  signedUrl: string,
  file: File,
  path: string,
  onError: (err: unknown) => void,
): Promise<void> {
  try {
    // marked so the global fetch wrapper leaves a failure to the callers, which queue a
    // transient one for a later retry and report anything else themselves (df-28f906a7558b,
    // Sentry 3XY). The mark also skips the wrapper's online check, so each caller runs that
    // check itself after a rejected PUT.
    const response = await fetch(signedUrl, markCallerReportsFailure({
      method: 'PUT',
      headers: { 'Content-Type': 'image/jpeg' },
      body: file,
    }));
    // surface a failed upload instead of saving an image that points at a
    // missing or broken GCS object (CLAUDE.md: unexpected conditions must surface)
    if (!response.ok) {
      throw new ImageUploadHttpError(response.status, path);
    }
  } catch (err) {
    // report to Sentry so a broken upload reaches the dev team, not just the
    // user's alert (a CORS-blocked PUT, issue #3707, rejects the fetch with a
    // TypeError that previously vanished into a user-only alert with no
    // telemetry), then rethrow so the caller's failure handling (the
    // user-facing message) still runs.
    onError(err);
    throw err;
  }
}
